import { useEffect, useState } from 'react';
import { useCartStore } from '../store/cartStore';
import { storeService } from '../services/storeService';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { GarmentTag } from '../components/ui/GarmentTag';
import { SellerPicker } from '../components/checkout/SellerPicker';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';
import { safeImageUrl } from '../utils/url';
import { productPlaceholderIcon } from '../utils/productPlaceholder';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { StoreSeller } from '../models';
import { PROMOTION_CAMPAIGN_ENABLED, getCampaignCatalogHref } from '../config/promotionCampaign';

function BackButton() {
  return (
    <Link
      to={getCampaignCatalogHref()}
      className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full transition-all duration-150"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      Voltar para os produtos
    </Link>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4 text-olivine-dark shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg className="w-4 h-4 text-olivine-dark shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function GiftIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18C2.754 7.5 2.25 8.004 2.25 8.625v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeWidth={2.5} d="M5 12h14" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CartItemQuantityPill({
  quantity,
  stockQuantity,
  onDecrease,
  onIncrease,
}: {
  quantity: number;
  stockQuantity: number;
  onDecrease: () => void;
  onIncrease: () => void;
}) {
  return (
    <div className="inline-flex items-center border-[1.5px] border-champagne-dark rounded-full w-fit">
      <button
        onClick={onDecrease}
        disabled={quantity <= 1}
        aria-label="Diminuir quantidade"
        className="w-8 h-8 flex items-center justify-center text-text-dark disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <MinusIcon />
      </button>
      <span className="w-6 text-center text-text-dark font-bold text-sm">{quantity}</span>
      <button
        onClick={onIncrease}
        disabled={quantity >= stockQuantity}
        aria-label="Aumentar quantidade"
        className="w-8 h-8 flex items-center justify-center text-text-dark disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <PlusIcon />
      </button>
    </div>
  );
}

function WhatsAppIcon({ className = 'w-4 h-4 text-olivine-dark shrink-0' }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L.057 23.535a.75.75 0 0 0 .916.928l5.803-1.524A11.938 11.938 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.587-.5-5.084-1.375l-.361-.214-3.742.981.998-3.648-.235-.374A9.963 9.963 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  );
}

function CartTrustBadges() {
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-6">
      <GarmentTag className="sm:-rotate-1">
        <ShieldIcon />
        <span className="text-text-medium">Compra segura</span>
      </GarmentTag>
      <GarmentTag className="sm:rotate-1">
        <RefreshIcon />
        <span className="text-text-medium">Troca fácil</span>
      </GarmentTag>
      <GarmentTag className="sm:-rotate-1">
        <WhatsAppIcon />
        <span className="text-text-medium">Frete combinado no WhatsApp</span>
      </GarmentTag>
    </div>
  );
}

export function CartPage() {
  const { items, removeItem, updateQuantity, toggleGift, totalPrice, totalItems, clearCart } = useCartStore();
  const navigate = useNavigate();

  const [stockChecking, setStockChecking] = useState(true);
  const [removedItems, setRemovedItems] = useState<{ productName: string; variantLabel: string }[]>([]);
  const [reducedStockItems, setReducedStockItems] = useState<Map<string, number>>(new Map());

  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [selectedSeller, setSelectedSeller] = useState<StoreSeller | null>(null);
  const [handOffSent, setHandOffSent] = useState(false);

  useEffect(() => {
    if (!PROMOTION_CAMPAIGN_ENABLED) {
      setLoadingSellers(false);
      return;
    }
    storeService
      .getSellers()
      .then((data) => {
        setSellers(data);
        if (data.length === 1) {
          setSelectedSeller(data[0]);
        }
      })
      .finally(() => setLoadingSellers(false));
  }, []);

  const handleForwardToSeller = () => {
    if (!selectedSeller) return;
    const url = buildWhatsAppUrl(selectedSeller, items);
    window.open(url, '_blank', 'noopener,noreferrer');
    setHandOffSent(true);
  };

  useEffect(() => {
    if (items.length === 0) {
      setStockChecking(false);
      return;
    }

    const uniqueProductIds = Array.from(new Set(items.map((i) => i.productId)));

    Promise.all(uniqueProductIds.map((id) => storeService.getProduct(id).catch(() => null))).then(
      (products) => {
        const removed: { productName: string; variantLabel: string }[] = [];
        const reduced = new Map<string, number>();

        const toRemove: { productId: number; variantId: number }[] = [];

        items.forEach((cartItem) => {
          const product = products.find((p) => p?.id === cartItem.productId);
          if (!product) return;
          const variant = product.variants.find((v) => v.id === cartItem.variantId);
          if (!variant) return;

          const key = `${cartItem.productId}-${cartItem.variantId}`;
          if (variant.stockQuantity === 0) {
            removed.push({ productName: cartItem.productName, variantLabel: cartItem.variantLabel });
            toRemove.push({ productId: cartItem.productId, variantId: cartItem.variantId });
          } else if (variant.stockQuantity < cartItem.quantity) {
            reduced.set(key, variant.stockQuantity);
          }
        });

        setRemovedItems(removed);
        setReducedStockItems(reduced);
        setStockChecking(false);

        if (toRemove.length > 0) {
          setTimeout(() => {
            toRemove.forEach(({ productId, variantId }) => removeItem(productId, variantId));
          }, 3000);
        }
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-4"><BackButton /></div>
        <EmptyState
          title="Carrinho vazio"
          description="Adicione produtos ao carrinho para continuar."
          action={<Link to={getCampaignCatalogHref()}><Button>Ver produtos</Button></Link>}
        />
      </div>
    );
  }

  const itemCount = totalItems();

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-dark">Sua sacola</h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-text-light">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</p>
            <button
              onClick={clearCart}
              className="text-sm text-danger hover:text-danger/80 transition-colors"
            >
              Esvaziar carrinho
            </button>
          </div>
        </div>
        <BackButton />
      </div>

      {stockChecking && (
        <p className="text-sm text-text-light text-center py-2">Verificando disponibilidade...</p>
      )}

      {!stockChecking && removedItems.length > 0 && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-2">
          <p className="text-sm font-semibold text-red-700 mb-1">
            {removedItems.length === 1 ? 'Item indisponível no estoque:' : 'Itens indisponíveis no estoque:'}
          </p>
          <ul className="flex flex-col gap-1">
            {removedItems.map((item, idx) => (
              <li key={idx} className="text-sm text-red-800">
                <span className="font-medium">{item.productName}</span> — {item.variantLabel}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!stockChecking && reducedStockItems.size > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-2">
          <p className="text-sm font-semibold text-amber-700 mb-2">
            Quantidade ajustada por disponibilidade:
          </p>
          <div className="flex flex-col gap-2">
            {items
              .filter((i) => reducedStockItems.has(`${i.productId}-${i.variantId}`))
              .map((item) => {
                const available = reducedStockItems.get(
                  `${item.productId}-${item.variantId}`
                )!;
                return (
                  <div
                    key={`${item.productId}-${item.variantId}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {item.imageUrl && (
                        <img
                          src={safeImageUrl(item.imageUrl)}
                          alt={item.productName}
                          className="w-8 h-8 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <span className="text-sm text-amber-800 truncate">
                        <span className="font-medium">{item.productName}</span> —{' '}
                        {item.variantLabel}
                        <span className="text-xs ml-1">(disponível: {available})</span>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        updateQuantity(item.productId, item.variantId, available);
                        setReducedStockItems((prev) => {
                          const m = new Map(prev);
                          m.delete(`${item.productId}-${item.variantId}`);
                          return m;
                        });
                      }}
                      className="text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap underline"
                    >
                      Ajustar
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {items.map((item) => (
          <div key={`${item.productId}-${item.variantId}`} className="bg-white rounded-xl p-4 flex gap-3 shadow-sm">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
              {item.imageUrl ? (
                <img src={safeImageUrl(item.imageUrl)} alt={item.productName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <img src={productPlaceholderIcon(item.productId)} alt="" className="w-10 h-10 opacity-70" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <p className="font-bold text-text-dark text-sm sm:text-base line-clamp-1">{item.productName}</p>
              {(item.colorName || item.variantLabel) && (
                <p className="text-text-light text-xs line-clamp-1">
                  {[item.colorName, item.variantLabel && `Tam. ${item.variantLabel}`].filter(Boolean).join(' · ')}
                </p>
              )}

              <button
                onClick={() => toggleGift(item.productId, item.variantId)}
                aria-pressed={!!item.isGift}
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border w-fit transition-colors ${
                  item.isGift
                    ? 'border-chocolate bg-champagne text-chocolate'
                    : 'border-champagne-dark text-text-light hover:border-chocolate/50 hover:text-chocolate'
                }`}
              >
                <GiftIcon className="w-3 h-3" />
                {item.isGift ? 'É presente' : 'Marcar como presente'}
              </button>

              <div className="flex items-center justify-between gap-2 mt-1">
                <CartItemQuantityPill
                  quantity={item.quantity}
                  stockQuantity={item.stockQuantity}
                  onDecrease={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                  onIncrease={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                />
                <span className="text-chocolate font-bold text-sm sm:text-base whitespace-nowrap">
                  R$ {formatCurrency(item.promotionalPrice ?? item.price)}
                </span>
              </div>

              {item.quantity >= item.stockQuantity && (
                <span className="text-xs text-warning">Limite de estoque</span>
              )}

              <button
                onClick={() => removeItem(item.productId, item.variantId)}
                className="text-text-light hover:text-danger text-xs underline transition-colors w-fit mt-0.5"
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      <CartTrustBadges />

      <div className="mt-6 bg-champagne-light rounded-xl p-4 shadow-sm">
        <div className="flex flex-col gap-1.5 text-sm text-text-medium">
          <div className="flex justify-between items-center">
            <span>Subtotal</span>
            <span>R$ {formatCurrency(totalPrice())}</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Frete</span>
            <span>Combinado no WhatsApp</span>
          </div>
        </div>
        <div className="flex justify-between items-center font-bold text-text-dark text-lg border-t border-dashed border-champagne-dark mt-2 pt-2">
          <span>Total</span>
          <span>R$ {formatCurrency(totalPrice())}</span>
        </div>
        {PROMOTION_CAMPAIGN_ENABLED ? (
          <div className="mt-4">
            <SellerPicker
              sellers={sellers}
              loading={loadingSellers}
              selectedSeller={selectedSeller}
              onSelect={(seller) => {
                setSelectedSeller(seller);
                setHandOffSent(false);
              }}
            />
            <Button
              fullWidth
              size="lg"
              onClick={handleForwardToSeller}
              disabled={stockChecking || !selectedSeller}
            >
              <WhatsAppIcon className="w-4 h-4 text-white shrink-0" />
              Encaminhar para a vendedora
            </Button>
            {handOffSent && (
              <p className="text-sm text-olivine-dark text-center mt-2">
                Mensagem enviada! A vendedora vai confirmar com você.
              </p>
            )}
          </div>
        ) : (
          <Button
            fullWidth
            size="lg"
            className="mt-4"
            onClick={() => navigate('/checkout')}
            disabled={stockChecking}
          >
            Ir para pagamento
          </Button>
        )}
        <Link to={getCampaignCatalogHref()} className="block text-center mt-3">
          <Button fullWidth variant="ghost">
            Continuar comprando
          </Button>
        </Link>
      </div>
    </div>
  );
}
